import pick from 'lodash/pick';
import omit from 'lodash/omit';

import api from '@/services/api';
import Uploader from '@/services/uploader';
import denormalizeJsonApi from '@/utils/denormalizeJsonApi';
import track from '@/utils/segment';
import denormalize from "@/utils/denormalizeResponse";

export async function openProject({ commit }, projectId) {
    commit('openProjectStart', projectId)
    commit('openProjectSuccess', projectId)
}

export async function getVideos({ commit, dispatch, state }) {
    const response = await api.get(`/api/v2/me`, {
        query: {
            include: {
                "@fragments": ['athlete-profile:videos'],
                videos: {
                    "@paginate": {
                        limit: 2000,
                        offset: 0, //(page * 100) || 0
                    },
                },
            },
        },
    });

    const videos = denormalize(response).root.videos;
    commit('getVideosSuccess', videos)

    videos.forEach(video => {
        // The video is now transcoded, so if we had it focused, actually start editing it.
        if (['aws', 'connectsports', 'video'].includes(video.host) && state.focusedLegacyVideoId == video.id) {
            dispatch('startEditingVideo', video.id)
        }
    })
}

// this is needed because when we upload a pano clip the transcoding job is not created until the video pipeline finishes
// and that is handled async by the queue, so it could take a while for the transcoding job to be created for the upload
export async function pollTranscodingJobsForUpload({ dispatch, state }, uploadId) {
    var interval = setInterval(async () => {
        const transcodingJobs = await dispatch('getTranscodingJobs');
        const hasTranscodingJobForUpload = transcodingJobs.findIndex(job => job.uploads.findIndex(({id}) => id == uploadId) >= 0) >= 0;

        if (hasTranscodingJobForUpload) {
            clearInterval(interval);
        }
    }, 5000);
}

export async function getTranscodingJobs({ commit }) {
    const response = await api.get('/api/v2/me', {
        query: {
            include: {
                transcodingJobs: {
                    apiId: true,
                    id: true,
                    aws_video_id: true,
                    status: true,
                    progress: true,
                    createdAt: true,
                    awsVideo: {
                        isUpload: true,
                        isVideoProject: true,
                        name: true,
                        videoRelationId: true,
                        videoProject: {
                            id: true,
                        }
                    },
                    uploads: {
                        id: true,
                    },
                    willBeRetried: true,
                },
            },
        },
    });
    const results = denormalize(response);

    const transcodingJobs = Object.values(results.resources.transcodingJobs);

    commit('setTranscodingJobs', transcodingJobs);

    return transcodingJobs;
}

export async function deleteFailedVideos({ commit, state }) {
    const videos = state.transcodingJobs
        .filter(job => job.status === 'failed')
        .map(job => job.video)

    const videoIds = videos.map(video => video.id)

    commit('deleteVideosStart', videoIds)

    await api.post('/api/v1/videos/delete', { videoIds })

    commit('deleteVideosSuccess', videoIds)
}

export async function getClips({ commit, state }) {
    const projectId = state.activeProjectId;
    const response = await api.get(`/api/v2/me`,
        {
            query: {
                include: {
                    '@fragments': [ 'athlete-profile:videoProjects' ],
                },
            },
        })
    

    const clips = denormalize(response)
        .root
        .videoProjects.find(vp => vp.id == projectId)
        .clips;

    commit('getClipsSuccess', clips);

    return response;
}

export function startUpload({ commit, dispatch }, { file, intent }) {
    const upload = Uploader.upload(file, intent, async() => {
        await dispatch('getTranscodingJobs')
        commit('uploadFinish', upload)

        track('added_video', {
            page: location.href,
            action: 'clicked',
            type: 'upload',
            site_access_id: api.clientData('site_access_id'),
        })
    })

    commit('uploadStart', upload)
}

export function cancelUpload({ state, commit }, upload) {
    if (state.isClipUploadDialogOpen) {
        commit('setIsClipUploadDialogOpen', false);
        commit('setPanoramicUploadStatus', false);
        commit('setPanoramicClipData', null);
    }

    Uploader.cancel(upload)

    commit('uploadCancel', upload)
}

export function failedUpload({ state, commit, dispatch }, upload) {
    commit('toggleFailedUpload', upload);

    if (state.isClipUploadDialogOpen) {
        commit('setIsClipUploadDialogOpen', false);
        commit('setPanoramicUploadStatus', false);
        commit('setPanoramicClipData', null);
    }

    commit('uploadCancel', upload);
}

export function cancelTranscodingJob({ state, commit, getters }, jobId) {
    const job = state.transcodingJobs.find(({id}) => id == jobId);
    const uploadId = job && job.uploads[0] ? job.uploads[0].id : null;
    const clip = uploadId ? state.clips.find(({aws_upload_id}) => aws_upload_id === uploadId) : null;

    api.delete(`/api/v1/transcoding-jobs/${jobId}`)
        .then(() => {
            commit('transcodingJobCanceled', jobId);
            if (clip) {
                commit('deleteClipSuccess', { id: clip.id });
            }
        })
        .catch(() => console.warn('Failed to cancel transcoding job'));
}

export function focusVideo({ dispatch, state }, videoId) {
    const video = state.videos.find(v => v.id == videoId)

    if (video.host === 'legacy') {
        dispatch('focusLegacyVideo', videoId)

        const hasOngoingTranscodingJob = video.legacyUploads
            .filter(upload => upload.video && upload.video.transcodingJob)
            .map(upload => upload.video.transcodingJob)
            .find(job => job.status !== 'failed')

        if (!video.startedTranscoding && !hasOngoingTranscodingJob) {
            dispatch('startTranscodingVideo', videoId)
        }
    } else {
        dispatch('startEditingVideo', videoId)
    }
}

export async function startTranscodingVideo({ commit }, videoId) {
    commit('transcodeRequestStart', { videoId })

    const response = await api.post(`/api/v1/transcoding-jobs`, {
        playerVideoId: videoId,
        intent: 'reel_library_video',
    })

    const transcodingJob = denormalizeJsonApi(response)

    commit('transcodeRequestSuccess', { videoId, transcodingJob })
}

export function startPlaybackFromBeginning({ getters, dispatch }) {
    const firstClipId = getters.playlist[0].id
    dispatch('jumpToAndPlayClip', firstClipId)
}

export function endClip({ state, getters, dispatch }) {
    if (state.playingClipId) {
        const playlist = getters.playlist

        const currentIndex = playlist.findIndex(c => c.id == state.playingClipId)
        const nextIndex = currentIndex + 1

        if (nextIndex >= playlist.length) {
            dispatch('stopPlayback')
        } else {
            dispatch('jumpToAndPlayClip', playlist[nextIndex].id)
        }
    } else if (state.workingClip) {
        dispatch('stopPlayback')
    }
}

export function stopPlayback({ commit }) {
    commit('playbackEnded')
}

export function pause({ state, commit }) {
    if (!state.isRecording) {
        commit('paused');
    }
}

export function resume({ commit, getters, state }) {
    if (getters.playingClip) {
        commit('resumed')
    } else if (state.workingClip) {
        commit('resumed')
    }
}

export function setPlaybackRate({ commit }, rate) {
    commit('setPlaybackRate', rate)
}

export function jumpToAndPlayClip({ commit, dispatch }, clipId) {
    commit('jumpToClip', clipId)
    dispatch('resume')
}

export function focusLegacyVideo({ state, commit, dispatch }, videoId) {
    if (state.playingClipId) {
        dispatch('stopPlayback')
    }

    commit('legacyVideoFocus', videoId)
}

export function startEditingVideo({ state, commit, dispatch }, videoId) {
    if (state.playingClipId) {
        dispatch('stopPlayback')
    }

    const video = state.videos.find(v => v.id == videoId)

    const clip = {
        video,
        name: video.name,
        rating: null,
        start_time: null,
        end_time: null,
        id: null,
    }

    commit('startedEditingClip', clip)
}

export function startEditingClip({ state, commit, dispatch }, clipId) {
    if (state.playingClipId) {
        dispatch('stopPlayback')
    }

    const clip = state.clips.find(c => c.id == clipId)
    commit('startedEditingClip', clip)
}

export function focusTranscodingClip({ state, commit, dispatch }, clipId) {
    if (state.playingClipId) {
        dispatch('stopPlayback')
    }

    const clip = state.clips.find(c => c.id == clipId)
    commit('startedFocusingTranscodingClip', clip)
}

export function stopEditingClip({ commit }) {
    commit('stoppedEditingClip')
}

export async function openClipSaveDialog({ commit, dispatch }, panoramicClip) {
    if (panoramicClip) {
        commit('setPanoramicClipData', panoramicClip);
    }

    dispatch('pause');
    commit('openedClipSaveDialog');
}

export async function cancelSaveClip({ commit }) {
    commit('setPanoramicClipData', null);
    commit('canceledSaveClip')
}

export async function confirmSaveClip({ dispatch, state }, data) {
    await dispatch('saveNewClip', data ? data : state.workingClip)
}

export async function openPublishDialog({ commit, dispatch }) {
    dispatch('pause')
    commit('openedPublishDialog')
}

export async function confirmPublish({ commit, state }, data) {
    commit('publishStart')

    await api.post('/api/v1/video-project/publish', { ...data, id: state.activeProjectId })
        .catch(({ status, response }) => {
            if (status === 422) {
                commit('publishError', response.errors);
            } else {
                commit('publishError', {});
            }
        })
        .then(() => {
            commit('publishSuccess')

            swal({
                title: 'You made it!',
                text: 'The video is being processed. It may take a few minutes and will be visible to every college coach in the country soon!',
                type: 'success',
                confirmButtonText: 'Go to Video Library',
                cancelButtonText: 'Back to project',
                showCancelButton: true,
                allowOutsideClick: true,
                cancelButtonClass: 'sr-close-button mr-2',
                confirmButtonClass: "sr-confirm-button",
                buttonsStyling: false,
                reverseButtons: true,
            }).then(isConfirm => {
                if (isConfirm) {
                    window.location.href = '/video'
                }
            });
        });
}

export async function cancelPublish({ commit }) {
    commit('canceledPublish')
}

export async function saveClip({ state, dispatch, getters }) {
    if (getters.isWorkingClipNew) {
        await dispatch('openClipSaveDialog')
    } else {
        await dispatch('updateClip', state.workingClip)
    }
}

export async function duplicateClip({ state, dispatch }, clipId) {
    const clip = state.clips.find(c => c.id == clipId)
    dispatch('saveNewClip', {
        ...clip,
    })
}

export async function saveNewClip({ state, commit, dispatch }, clip) {
    const projectId = state.activeProjectId;

    let preSavedClip = omit(clip, 'video.category'); //remove category so the view switcher dropdown doesn't show when playing the new clip

    commit('isSaving')
    commit('saveNewClipStart', preSavedClip)

    const response = await api.post(`/api/v1/video-projects/${projectId}/clips`, {
        name: clip.name || null,
        description: clip.description || null,
        player_video_id: clip.isNewClip ? null : clip.video.id,
        aws_upload_id: clip.isNewClip ? clip.aws_upload_id : null,
        start_time: clip.isNewClip ? null : clip.start_time,
        end_time: clip.isNewClip ? null : clip.end_time,
        rating: clip.rating || null,
    })

    const savedClip = {
        ...denormalizeJsonApi(response),
        aws_upload_id: clip.isNewClip ? clip.aws_upload_id : null,
        video: clip.isNewClip ? null : preSavedClip.video,
    }

    commit('saveNewClipSuccess', savedClip)
    commit('isSaving')
    dispatch('startEditingVideo', savedClip.video.id)
}

export async function uploadPanoClip({ state, commit, dispatch }, clipFormData) {
    commit('setPanoramicUploadStatus', clipFormData.name);
    commit('canceledSaveClip');
    commit('setIsClipUploadDialogOpen', true);

    let upload = Uploader.upload(new File([state.panoramicClipData], clipFormData.name, {'type':'video/webm'}), 'video_clip', async (upload) => {
        await dispatch('pollTranscodingJobsForUpload', upload.id);

        dispatch('saveNewClip', {...clipFormData, ...{aws_upload_id: upload.id, isNewClip: true}});

        commit('setIsClipUploadDialogOpen', false);
        commit('setPanoramicUploadStatus', false);
        commit('setPanoramicClipData', null);
        commit('uploadFinish', upload);
    }, () => {}, (error, upload) => {
        dispatch('failedUpload', upload);
    });

    upload.intent = 'video_clip';

    commit('uploadStart', upload);
}

export async function updateClip({ state, commit }, { id, ...fields }) {
    const projectId = state.activeProjectId

    commit('updateClipStart', { id, ...fields })
    commit('isClipUpdating')
    commit('isSaving')
    const response = await api.put(`/api/v1/video-projects/${projectId}/clips/${id}`, fields)

    const responseClip = denormalizeJsonApi(response)
    commit('updateClipSuccess', { id, ...pick(responseClip, Object.keys(fields)) })
    commit('isClipUpdating')
    commit('isSaving')
}

export async function deleteClip({ state, commit }, clipId) {
    const projectId = state.activeProjectId
    const clip = state.clips.find(c => c.id == clipId);
    const transcodingJob = clip.aws_upload_id ? state.transcodingJobs.find(({uploads}) => uploads.filter(({id}) => Number(clip.aws_upload_id) === Number(id)).length >= 1) : null;

    commit('deleteClipStart', { id: clipId })
    commit('isSaving')

    await api.delete(`/api/v1/video-projects/${projectId}/clips/${clipId}`).then(() => {
        if (transcodingJob) {
            commit('transcodingJobCanceled', transcodingJob.id)
        }
    })

    commit('deleteClipSuccess', { id: clipId })
    commit('isSaving')
}

export async function updateClipRating({ dispatch }, { id, rating }) {
    await dispatch('updateClip', { id, rating })
}

export async function reorderClips({ state, commit }, clips) {
    commit('reorderClipsStart', clips)
    commit('isSaving')

    const projectId = state.activeProjectId
    await api.post(`/api/v1/video-projects/${projectId}/clips/reorder`, { clips })

    commit('reorderClipsSuccess', clips)
    commit('isSaving')
}

export async function toggleLibrary({ commit }) {
    commit('toggleLibrary');
}

export async function openInfoModal({ commit }) {
    commit('openedInfoModal')
}

export async function closeInfoModal({ commit }) {
    commit('closedInfoModal')
}

export async function getProjects({ commit }) {
    const response = await api.get(`/api/v1/video-project`)
    const projects = denormalizeJsonApi(response)

    commit('getProjectsSuccess', projects)
}

export async function saveNewProject({ commit, state }, projectName) {
    state.isLoading = true;
    commit('updateProjects')

    const response = await api.post('/api/v1/video-project/add', {
        name: projectName,
    })
    const updatedProjects = denormalizeJsonApi(response);
    // commit('updateProjectsSuccess', updatedProjects);

    window.location.href = window.location.href + '/' + updatedProjects[(updatedProjects.length - 1)].id;
}

export async function updateProjectTitle({ commit }, payload) {
    commit('updateProjects')
    const response = await api.post('/api/v1/video-project/update', {
        name: payload.title,
        id: payload.projectId,
    })

    const updatedProjects = denormalizeJsonApi(response);
    commit('updateProjectsSuccess', updatedProjects)

}

export async function duplicateProject({ commit }, projectId) {
    commit('isLoading')
    commit('updateProjects')

    const response = await api.post('/api/v1/video-project/duplicate', {
        id: projectId,
    })

    const updatedProjects = denormalizeJsonApi(response);
    commit('updateProjectsSuccess', updatedProjects)
    commit('isLoading')

}

export async function deleteProject({ commit }, projectId) {
    commit('isLoading')
    commit('updateProjects')

    const response = await api.post('/api/v1/video-project/delete', {
        id: projectId,
    })

    const updatedProjects = denormalizeJsonApi(response);
    commit('updateProjectsSuccess', updatedProjects)
    commit('isLoading')

}
